import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { OptionType } from "../../../../content/static/onboardingQuestions";
import { AppText, FlexDiv } from "../../../ui";
import { useOnboardingContext } from "../OnboardingContext";
import theme from "../../../../util/theme";
import { mobileSize } from "../../../../util/variables";
import { web_acq_question_click } from "../../../../services/mixpanel/acquisition-events";
import { getTextFromReactNode } from "../../../../util/misc";

interface MultiselectQuestionProps {
  id: string;
  title: string;
  subtitle: string;
  options: OptionType[];
  answer_key: string;
  textAlign?: "left" | "center" | "right";
  singleColumn?: boolean;
}

interface MultiSelectProps {
  label: string;
  selected: boolean;
  updateChoice: (value: string, exists: boolean, isNoneOption?: boolean) => void;
  image?: string;
  isNoneOption?: boolean;
}

const MultiselectQuestion: React.FC<MultiselectQuestionProps> = ({
  id,
  title,
  subtitle,
  options,
  answer_key,
  textAlign = "center",
  singleColumn = false,
}) => {
  const [choices, setChoices] = useState<string[] | React.ReactNode[]>([]);
  const { updateOnboardingAnswer } = useOnboardingContext();

  useEffect(() => {
    const choicesText = choices.map(choice => getTextFromReactNode(choice)).join(", ");
    updateOnboardingAnswer(answer_key, choicesText);
  }, [choices, answer_key]);

  const updateChoice = React.useCallback(
    (value: string, exists: boolean, isNoneOption?: boolean) => {
      if (exists) {
        // remove choice from array
        const idx = choices.indexOf(value);
        const newChoices = choices.slice();
        newChoices.splice(idx, 1);
        setChoices(newChoices);
        return;
      }

      // add choice to array
      if (isNoneOption) {
        // If none option selected, clear other choices
        setChoices([value]);
      } else {
        // If regular option selected, remove none option if present
        const newChoices = choices.filter(choice =>
          !options.find(opt => opt.label === choice && opt.isNoneOption)
        );
        setChoices([...newChoices, value]);
      }

      web_acq_question_click(id);
    },
    [choices, id, options],
  );

  return (
    // <Question>
    <FlexDiv
      direction="column"
      align="center"
      style={{ position: "relative", textAlign: "center", overflow: "hidden", height: "100%" }}
    >
      <AppText
        noMargin
        fontSize={24}
        mobileFontSize={20}
        fontWeight={500}
        textAlign={textAlign}
        style={{ width: "90vw", maxWidth: 468 }}
      >
        {title}
      </AppText>
      {subtitle && <Subtitle textAlign={textAlign}>{subtitle}</Subtitle>}
      {/* <div style={{ position: "relative", maxHeight: "calc(100% - 126px)" }}> */}
      <OptionsContainer singleColumn={singleColumn}>
        {options.length > 0 &&
          options.map(option => (
            <MultiSelect
              key={option.label}
              label={option.label}
              image={option.image}
              updateChoice={updateChoice}
              selected={choices.includes(option.label)}
              singleColumn={singleColumn}
              isNoneOption={option.isNoneOption}
            />
          ))}
      </OptionsContainer>
      <Shadow />
      {/* </div> */}
    </FlexDiv>
  );
};

const Question = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
`;

const Subtitle = styled(AppText as any)`
  margin-top: 16px;
  @media ${mobileSize} {
    margin-top: 8px;
  }
`;

const OptionsContainer = styled.div<{ singleColumn?: boolean }>`
  position: relative;
  display: ${props => (props.singleColumn ? "flex" : "grid")};
  ${props => props.singleColumn && css`
    flex-direction: column;
    gap: 24px;
  `}

  ${props => !props.singleColumn && css`
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
  `}
  margin-top: ${props => (props.singleColumn ? 12 : 48)}px;
  max-height: calc(100% - 126px); // height of text + margins
  overflow-y: auto;
  padding: 4px 8px;

  @media ${mobileSize} {
    margin-top: ${props => (props.singleColumn ? 0 : 32)}px;
    gap: 16px;
    padding: 8px 32px 8px 32px;
    width: 100vw;
    /* box-shadow: inset 0px -4px -1px 2px rgba(0, 0, 0, 0.12); */
  }
`;

const Shadow = styled.div`
  display: none;
  position: absolute;
  bottom: 0;
  left: 0;
  height: 5px;
  width: 100vw;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  @media ${mobileSize} {
    display: block;
  }
`;

const MultiSelect: React.FC<MultiSelectProps> = ({ label, image, updateChoice, selected, isNoneOption, singleColumn }) => {
  return (
    <Container selected={selected} image={!!image} singleColumn={singleColumn} onClick={() => updateChoice(label, selected, isNoneOption)}>
      {image && <Img src={image} alt={`${label} icon`} width={56} height={56} />}
      <SelectText hasImage={!!image}>{label}</SelectText>
    </Container>
  );
};

const Container = styled.div<{ selected: boolean; image: boolean; singleColumn?: boolean }>`
  position: relative;
  ${props => !props.image && css`
    display: flex;
    align-items: center;
    justify-content: center;
  `}
  width: ${props => (props.singleColumn ? "468px" : "220px")};
  height: 86px;
  margin-top: ${props => (props.image ? 28 : 0)}px;
  background-color: ${props => (props.selected ? theme.SELECTED_COLOR : theme.WHITE_COLOR)};
  border: ${props => (props.selected ? `1px solid ${theme.SELECTED_BORDER_COLOR}` : "none")};
  border-radius: ${props => (props.singleColumn ? "8px" : "4px")};
  box-shadow: ${props =>
    props.selected ? "0px 1px 7px rgba(44, 39, 56, 0.14)" : "0px 2px 11px rgba(44, 39, 56, 0.14)"};
  cursor: pointer;

  @media ${mobileSize} {
    width: auto;
    height: ${props => (props.singleColumn ? 64 : 54)}px;
    min-height: ${props => (props.singleColumn ? 64 : 54)}px;
    padding: 0px 12px 2px 12px;
    font-size: 10px;
  }
`;

const Img = styled.img`
  position: absolute;
  top: -28px;
  left: 50%;
  translate: -50% 0%;
  cursor: pointer;
  @media ${mobileSize} {
    height: 40px;
    width: 40px;
  }
`;

const SelectText = styled(AppText as any) <{ hasImage?: boolean }>`
  margin: 0;
  margin-top: ${props => (props.hasImage ? 36 : 0)}px;
  @media ${mobileSize} {
    font-size: 16px;
    line-height: 18px;
    margin-top: ${props => (props.hasImage ? 17 : 0)}px;
  }
`;

export default MultiselectQuestion;
